<template>
  <div>
    <label
      v-if="label"
      :for="computedId"
      class="fe-block fe-text-body-s-bold lg:fe-text-heading-xxs"
    >
      {{ labelText }}
    </label>
    <textarea
      ref="textarea"
      :id="computedId"
      :name="name"
      :value="value"
      :placeholder="placeholder"
      :minlength="minLength"
      :maxlength="maxLength"
      :disabled="disabled"
      :autofocus="autoFocus"
      :required="required"
      :rows="rows"
      @focus="$emit('focus', $event)"
      @blur="$emit('blur', $event)"
      @input="handleInput"
      @change="$emit('change', $event.target.value)"
      @keydown="$emit('keydown', $event)"
      :class="[
        'fe-block fe-w-full fe-py-xs fe-bg-transparent fe-border-0 fe-border-b-xxxs fe-text-body-s sm:fe-text-body-xxs lg:fe-mt-s placeholder:fe-text-primary-grey-80 hover:fe-border-primary-50 focus:fe-outline-none focus:fe-border-primary-60 fe-resize-y fe-overflow-hidden',
        stateClass,
        { 'disabled:fe-border-primary-grey-80': disabled },
      ]"
    />
    <small
      v-if="stateText"
      class="fe-block fe-text-body-xs fe-mt-xxs"
      :class="stateTextClass"
    >
      {{ stateText }}
    </small>
    <small v-if="helperText" class="fe-text-primary-grey-80 fe-text-body-xs">
      {{ helperText }}
    </small>
  </div>
</template>

<script>
  import { generateRandomId } from "@/utils/utils"

  export default {
    props: {
      label: {
        type: String,
        default: "",
      },
      helperText: {
        type: String,
        default: "",
      },
      name: {
        type: String,
        default: "",
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      state: {
        type: String,
        default: "none",
        validator: (value) =>
          ["none", "success", "error", "warning"].includes(value),
      },
      stateText: {
        type: String,
        default: "",
      },
      id: {
        type: String,
        default: "",
      },
      value: {
        type: String,
        default: "",
      },
      required: {
        type: Boolean,
        default: false,
      },
      placeholder: {
        type: String,
        default: "",
      },
      minLength: {
        type: Number,
        default: undefined,
        required: false,
      },
      maxLength: {
        type: Number,
        default: undefined,
        required: false,
      },
      autoFocus: {
        type: Boolean,
        default: false,
      },
      rows: {
        type: Number,
        default: 1,
      },
    },
    computed: {
      computedId() {
        return this.id || `textarea-${generateRandomId(6)}`
      },
      labelText() {
        return `${this.label}${this.required ? "*" : ""}`
      },
      stateClass() {
        return {
          "fe-border-primary-grey-100": this.state === "none" || !this.state,
          "fe-border-success-50": this.state === "success",
          "fe-border-error-50": this.state === "error",
          "fe-border-warning-50": this.state === "warning",
        }
      },
      stateTextClass() {
        return {
          "fe-text-success-80": this.state === "success",
          "fe-text-error-80": this.state === "error",
          "fe-text-warning-80": this.state === "warning",
        }
      },
    },
    watch: {
      value() {
        this.$nextTick(() => this.autoResize())
      },
    },
    mounted() {
      this.autoResize() // Ensure the textarea is resized when the component is mounted
    },
    methods: {
      autoResize() {
        const textarea = this.$refs.textarea
        textarea.style.height = "auto" // Reset height to calculate the new height
        textarea.style.height = `${textarea.scrollHeight}px` // Set the height to scroll height
      },
      handleInput(event) {
        this.$emit("input", event.target.value)
        this.autoResize()
      },
    },
  }
</script>
